
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, X } from "lucide-react";
import { BlogPost, BlogPostFormData, BlogCategory } from "@/types/blogPost";
import { fetchBlogCategories, uploadBlogImage } from "@/utils/blogOperations";
import { useToast } from "@/hooks/use-toast";
import { RichTextEditor } from "./RichTextEditor";
import { BlogPostScheduler } from "./BlogPostScheduler";

interface BlogPostFormProps {
  isEditing: boolean;
  editingPost: BlogPost | null;
  onSubmit: (formData: BlogPostFormData) => Promise<void>;
  onCancel: () => void;
}

export function BlogPostForm({ isEditing, editingPost, onSubmit, onCancel }: BlogPostFormProps) {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [formData, setFormData] = useState<BlogPostFormData>({
    title: "",
    subtitle: "",
    content: "",
    content_formatted: null,
    excerpt: "",
    featured_image_url: "",
    category_id: "",
    published: false,
    scheduled_for: undefined,
  });
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  // Atualizar formData quando editingPost mudar
  useEffect(() => {
    if (editingPost) {
      setFormData({
        title: editingPost.title || "",
        subtitle: editingPost.subtitle || "",
        content: editingPost.content || "",
        content_formatted: editingPost.content_formatted || null,
        excerpt: editingPost.excerpt || "",
        featured_image_url: editingPost.featured_image_url || "",
        category_id: editingPost.category_id || "",
        published: editingPost.published || false,
        scheduled_for: editingPost.scheduled_for || undefined,
      });
    } else {
      // Reset form quando não está editando
      setFormData({
        title: "",
        subtitle: "",
        content: "",
        content_formatted: null,
        excerpt: "",
        featured_image_url: "",
        category_id: categories[0]?.id || "",
        published: false,
        scheduled_for: undefined,
      });
    }
  }, [editingPost, categories]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await fetchBlogCategories();
      setCategories(data);
      
      // Se não há categoria selecionada e há categorias, selecionar a primeira
      if (!formData.category_id && data.length > 0 && !editingPost) {
        setFormData(prev => ({ ...prev, category_id: data[0].id }));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar categorias.",
        variant: "destructive",
      });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const imageUrl = await uploadBlogImage(file);
      setFormData({ ...formData, featured_image_url: imageUrl });
      toast({
        title: "Sucesso!",
        description: "Imagem enviada com sucesso.",
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Erro",
        description: "Erro ao enviar imagem.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, featured_image_url: "" });
  };

  const handleContentChange = (content: string, formatted: any) => {
    setFormData({ 
      ...formData, 
      content, 
      content_formatted: formatted 
    });
  };

  const handleScheduleChange = (scheduledDate: string | null) => {
    setFormData({ 
      ...formData, 
      scheduled_for: scheduledDate || undefined 
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
    
    // Só resetar o formulário se não estiver editando
    if (!isEditing) {
      setFormData({
        title: "",
        subtitle: "",
        content: "",
        content_formatted: null,
        excerpt: "",
        featured_image_url: "",
        category_id: categories[0]?.id || "",
        published: false,
        scheduled_for: undefined,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">
          {isEditing ? "Editar Post" : "Criar Novo Post"}
        </h3>
        {isEditing && (
          <Button onClick={onCancel} variant="outline">
            Cancelar
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="title">Título</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>

        <div>
          <Label htmlFor="subtitle">Subtítulo</Label>
          <Input
            id="subtitle"
            value={formData.subtitle}
            onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
          />
        </div>

        <div>
          <Label htmlFor="excerpt">Resumo</Label>
          <Textarea
            id="excerpt"
            value={formData.excerpt}
            onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
            rows={2}
          />
        </div>

        <div>
          <Label>Conteúdo</Label>
          <RichTextEditor
            value={formData.content}
            onChange={handleContentChange}
            placeholder="Escreva o conteúdo do post..."
            rows={12}
          />
        </div>

        <div>
          <Label>Imagem em Destaque</Label>
          {formData.featured_image_url ? (
            <Card className="mt-2">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <img
                    src={formData.featured_image_url}
                    alt="Preview"
                    className="w-20 h-20 object-cover rounded"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Imagem carregada</p>
                    <p className="text-xs text-gray-500 break-all">{formData.featured_image_url}</p>
                  </div>
                  <Button
                    type="button"
                    onClick={removeImage}
                    size="sm"
                    variant="destructive"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="mt-2">
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
              />
              {uploading && (
                <p className="text-sm text-gray-500 mt-1">Enviando imagem...</p>
              )}
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="category">Categoria</Label>
          <Select
            value={formData.category_id}
            onValueChange={(value) => setFormData({ ...formData, category_id: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione uma categoria" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded border"
                      style={{ 
                        backgroundColor: category.color_hex,
                        borderColor: category.border_color_hex
                      }}
                    />
                    {category.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <BlogPostScheduler
          scheduledFor={formData.scheduled_for}
          onScheduleChange={handleScheduleChange}
          disabled={uploading}
        />

        <div className="flex items-center space-x-2">
          <Switch
            id="published"
            checked={formData.published}
            onCheckedChange={(checked) => setFormData({ ...formData, published: checked })}
          />
          <Label htmlFor="published">Publicado</Label>
        </div>

        <Button type="submit" className="w-full" disabled={uploading}>
          <Plus className="w-4 h-4 mr-2" />
          {isEditing ? "Atualizar Post" : "Criar Post"}
        </Button>
      </form>
    </div>
  );
}
